// The parked screen's decisions, kept free of the DOM so they can be tested: what to save when you
// park, what the move-by card says, and what the one reminder is for.
import { esc, SIDE } from './ui.js'
import { local, when, clock, span, nightBefore, MIN, HOUR, DAY } from '../src/shared/time.ts'

/** What's saved when you tap "Parked here". No posted limit still means LA's 72-hour rule. */
export function parkedRecord(c, win, now = Date.now()) {
  const cap = now + 72 * HOUR
  const capped = c.stopKind === 'horizon' || c.stopAt > cap
  return {
    curb: { id: c.id, street: c.street, side: c.side, cross: c.cross, kind: c.kind, lat: c.lat, lon: c.lon, geom: c.geom },
    at: now, win, stopAt: capped ? cap : c.stopAt, stopWhy: capped ? '72-hour rule' : c.stopWhy, cost: c.cost,
  }
}

/** True when the meter comes back on before you'd have to move: that's the deadline that matters.
 * Not while it's already running: then "feed it now" comes first, not tomorrow's restart. */
export const meterFirst = (pk, now) => !!(pk.meterOn && pk.meterOn > now && (!pk.stopAt || pk.meterOn < pk.stopAt)) && !meterRunning(pk, now)

/** When the meter has been running since, if it is now: the server's answer, or the restart we knew of
 * once it's passed (the home banner and an offline card never hear back from the server). */
export function meterRunning(pk, now) {
  // The server says when the running meter stops (meterOff) and when the next paid stretch ends
  // (meterOnEnd). Records saved before it did get a guess: 8 hours, or 2 after a check.
  if (pk.stopAt && pk.stopAt <= now) return null // overdue: the card says "Move now" instead
  if (pk.meterSince && pk.meterSince <= now) {
    if (pk.meterOff ? now < pk.meterOff : now - pk.meterSince < 8 * HOUR || (pk.checked && now - pk.checked < 2 * HOUR)) return pk.meterSince
  }
  if (pk.meterOn && pk.meterOn <= now && (pk.meterOnEnd ? now < pk.meterOnEnd : now - pk.meterOn < 8 * HOUR)) return pk.meterOn
  return null
}

/** The home banner's one line: "Pay or move by 8am · meter back on", "Move by 10am · 2 hr limit", "No rush". */
export function deadlineLine(pk, now) {
  const feed = meterRunning(pk, now) ? ' · feed the meter' : ''
  if (meterFirst(pk, now)) return `Pay or move by ${when(pk.meterOn, now)} · meter back on`
  if (!pk.stopAt) return feed ? 'Feed the meter' : 'No rush'
  if (pk.stopAt <= now) return now - pk.stopAt < MIN ? `Move now · due ${when(pk.stopAt, now)}` : `Move now · ${span(now - pk.stopAt)} past ${when(pk.stopAt, now)}`
  return `Move by ${when(pk.stopAt, now)}${feed || (pk.stopWhy ? ` · ${pk.stopWhy.toLowerCase()}` : '')}`
}

/** The move-by card's HTML. */
export function moveBy(pk, now) {
  // The things to act on before the move-by time: not legal yet, or the meter running.
  const notes = []
  if (pk.opensAt && pk.opensAt > now) notes.push(`<span class="meter-on warn-text">Not legal until ${esc(when(pk.opensAt, now))}${pk.opensWhy ? ` (${esc(pk.opensWhy.toLowerCase())})` : ''}. Wait with the car until then.</span>`)
  if (meterFirst(pk, now)) {
    // The meter restarting is the real deadline: a glance should read 8am, not the 10am move-by.
    const then = pk.stopAt ? `Paying, move by ${when(pk.stopAt, now)}${pk.stopWhy ? ` (${pk.stopWhy.toLowerCase()})` : ''}` : 'Paying, nothing makes you move this week'
    return `<span class="label">Pay or move by</span><span class="big">${esc(when(pk.meterOn, now))}</span>
    <span class="muted">in ${esc(span(pk.meterOn - now))} · the meter's back on then. ${esc(then)}.</span>${notes.join('')}`
  }
  const since = meterRunning(pk, now)
  if (since) notes.push(`<span class="meter-on">The meter's been on since ${esc(when(since, now))}. Feed it, or move.</span>`)
  if (!pk.stopAt) {
    // Until the server's week-ahead look comes back, all we know is the plan's (a day past the stay).
    const head = pk.checked ? ['No limit this week', 'Nothing posted makes you move in the next 7 days'] : [`No limit through ${when(pk.win.end + DAY, now)}`, 'The rest of the week isn\'t checked yet']
    return `<span class="label">No rush</span><span class="big">${esc(head[0])}</span><span class="muted">${esc(head[1])}</span>${notes.join('')}`
  }
  const left = pk.stopAt - now
  if (left <= 0) {
    // Past it: say so plainly, in the warning colour, and keep any meter note.
    return `<span class="label">Move now</span><span class="big">Now</span>
    <span class="muted warn-text">${left > -MIN ? 'Your' : `${esc(span(-left))} past your`} move-by time (${esc(when(pk.stopAt, now))}${pk.stopWhy ? `, ${esc(pk.stopWhy.toLowerCase())}` : ''}).</span>${notes.join('')}`
  }
  return `<span class="label">Move by</span><span class="big">${esc(when(pk.stopAt, now))}</span>
    <span class="muted">in ${esc(span(left))}${pk.stopWhy ? ` · ${esc(pk.stopWhy.toLowerCase())}` : ''}</span>${notes.join('')}`
}

/** What the reminder is for: the meter coming back on, or the move-by time. Null if neither is 10+ min out. */
export function alertFor(pk, now = Date.now()) {
  if (pk.meterOn && pk.meterOn > now + 10 * MIN && (!pk.stopAt || pk.meterOn < pk.stopAt)) return { at: pk.meterOn, meter: true }
  return pk.stopAt && pk.stopAt > now + 10 * MIN ? { at: pk.stopAt, meter: false } : null
}

/** The body of POST /api/remind for that alert. */
export function remindArgs(pk, a) {
  const c = pk.curb
  return { at: a.at, street: c.street + (c.side ? ` (${SIDE[c.side]})` : ''), why: pk.stopWhy, when: clock(local(a.at).min), meter: a.meter, moveBy: a.meter && pk.stopAt ? when(pk.stopAt, a.at) : undefined, night: !a.meter && nightBefore(a.at, pk.stopWhy) }
}
